import { fetchApi } from '@inmagik/react-crud'
import classNames from 'classnames'
import dayjs from 'dayjs'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { useState } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import 'react-datepicker/dist/react-datepicker.css'
import { useAuth } from '../../auth'
import MaterialIcon from '../../components/MaterialIcon'
import Paginator from '../../components/Paginator'
import { SegmentedControlLink } from '../../components/SegmentedControlLink'
import SelectAutoCompleteFilter from '../../components/SelectAutocompleteFilter'
import { API_URL, ESITI, INTERVENTI_OPTIONS_TAB, STATI_INTERVENTO, TIPI_INTERVENTO } from '../../constants'
import { useQsFilters } from '../../hooks/filters'
import { useInterventiList } from '../../hooks/interventi'
import { areFiltersActive, interventiInitFilters } from './InterventiFilters'
import { InterventiTable } from './InterventiTable'
import ModalPianificazionePeriodica from './ModalPianificazionePeriodica'
import ModalPianificazioneTaratura from './ModalPianificazioneTaratura'
import ListFilters from '../../components/ListFilters'
import MobileActionsMenu from '../../components/MobileActionsMenu'
import DateRangeField from '../../components/DateRangeField'

export default function Interventi() {
  const { filters, uiFilters, setFilters } = useQsFilters(interventiInitFilters)
  const interventi = useInterventiList({
    search: filters.search,
    ordering: filters.ordering,
    page: String(filters.page),
    tipo_intervento: filters.tipo_intervento,
    stato: filters.stato,
    esito: filters.esito,
    cliente: filters.cliente,
    data_intervento__gte: filters.data_intervento__gte,
    data_intervento__lte: filters.data_intervento__lte,
    search_bilancia: filters.bilancia,
  })

  const { tokens } = useAuth()

  const [modalStatePianificaPeriodica, modalActionsPianificaPeriodica] = useModalTrigger()
  const [modalStatePianificaTaratura, modalActionsPianificaTaratura] = useModalTrigger()

  const [isDownloadInterventiPdf, setIsDownloadInterventiPdf] = useState(false)
  const [isDownloadInterventiXlsx, setIsDownloadInterventiXlsx] = useState(false)

  const [isDownloadCertTaratura, setIsDownloadCertTaratura] = useState(false)

  return (
    <div className="page page-list">
      <div className="row mt-4">
        {/* Stessa struttura dei calendari (Programmazione / Scadenze): evita salti di layout cambiando tab */}
        <div className="col-12 d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <h1 className="title">Interventi</h1>
          </div>
        </div>
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center list-header">
            <SegmentedControlLink
              value={INTERVENTI_OPTIONS_TAB.interventi.value}
              options={Object.values(INTERVENTI_OPTIONS_TAB)}
            />
            <div className="d-flex align-items-center list-header-actions">
              <MobileActionsMenu>
                <Button
                  disabled={isDownloadCertTaratura}
                  onClick={async () => {
                    setIsDownloadCertTaratura(true)
                    const data = await fetchApi(
                      `${API_URL}/bilance/intervento/download-cert-taratura-bulk/?${new URLSearchParams({
                        search: filters.search,
                        ordering: filters.ordering,
                        tipo_intervento: filters.tipo_intervento,
                        stato: filters.stato,
                        esito: filters.esito,
                        cliente: filters.cliente,
                        data_intervento__gte: filters.data_intervento__gte,
                        data_intervento__lte: filters.data_intervento__lte,
                        bilancia: filters.bilancia,
                      })}`,
                      {
                        headers: {
                          Authorization: `Bearer ${tokens?.access}`,
                        },
                      }
                    )
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = 'rapporti-di-taratura.zip'
                    a.click()
                    setIsDownloadCertTaratura(false)
                  }}
                  variant="outline-secondary"
                  className="d-flex align-items-center bg-white me-2 text-secondary"
                  type="button"
                >
                  <MaterialIcon icon="download" className="me-2" />
                  Scarica Rapp. Tarat.
                  {isDownloadCertTaratura && (
                    <Spinner animation="border" variant="secondary" className="ms-4" size="sm" />
                  )}
                </Button>

                <Button
                  disabled={isDownloadInterventiXlsx || (interventi.data && interventi?.data?.count > 1000)}
                  onClick={async () => {
                    setIsDownloadInterventiXlsx(true)
                    const data = await fetchApi(
                      `${API_URL}/bilance/intervento/download-xlsx-interventi/?${new URLSearchParams({
                        search: filters.search,
                        ordering: filters.ordering,
                        tipo_intervento: filters.tipo_intervento,
                        stato: filters.stato,
                        esito: filters.esito,
                        cliente: filters.cliente,
                        data_intervento__gte: filters.data_intervento__gte,
                        data_intervento__lte: filters.data_intervento__lte,
                        search_bilancia: filters.bilancia,
                      })}`,
                      {
                        headers: {
                          Authorization: `Bearer ${tokens?.access}`,
                        },
                      }
                    )
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url

                    a.download = 'elenco-interventi.xlsx'
                    a.click()
                    setIsDownloadInterventiXlsx(false)
                  }}
                  variant="outline-secondary"
                  className="d-flex align-items-center bg-white me-2 text-secondary"
                  type="button"
                >
                  <MaterialIcon icon="download" className="me-2" />
                  Scarica Excel
                  {isDownloadInterventiXlsx && (
                    <Spinner animation="border" variant="secondary" className="ms-4" size="sm" />
                  )}
                </Button>
                <Button
                  disabled={isDownloadInterventiPdf || (interventi.data && interventi?.data?.count > 1000)}
                  onClick={async () => {
                    setIsDownloadInterventiPdf(true)
                    const data = await fetchApi(
                      `${API_URL}/bilance/intervento/download-pdf-interventi/?${new URLSearchParams({
                        search: filters.search,
                        ordering: filters.ordering,
                        tipo_intervento: filters.tipo_intervento,
                        stato: filters.stato,
                        esito: filters.esito,
                        cliente: filters.cliente,
                        data_intervento__gte: filters.data_intervento__gte,
                        data_intervento__lte: filters.data_intervento__lte,
                        search_bilancia: filters.bilancia,
                      })}`,
                      {
                        headers: {
                          Authorization: `Bearer ${tokens?.access}`,
                        },
                      }
                    )
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url

                    a.download = 'elenco-interventi.pdf'
                    a.click()
                    setIsDownloadInterventiPdf(false)
                  }}
                  variant="outline-secondary"
                  className="d-flex align-items-center bg-white me-2 text-secondary"
                  type="button"
                >
                  <MaterialIcon icon="download" className="me-2" />
                  Scarica PDF
                  {isDownloadInterventiPdf && (
                    <Spinner animation="border" variant="secondary" className="ms-4" size="sm" />
                  )}
                </Button>

                <Button
                  onClick={() => modalActionsPianificaPeriodica.open()}
                  variant="white"
                  className="text-white d-flex align-items-center btn-outline-periodico"
                  type="button"
                >
                  <span
                    className="material-icons me-2"
                    style={{
                      fontSize: '16px',
                    }}
                  >
                    edit_calendar
                  </span>
                  <span> Pianifica periodica</span>
                </Button>
                <Button
                  onClick={() => {
                    modalActionsPianificaTaratura.open()
                  }}
                  variant="white"
                  className="text-white d-flex align-items-center ms-2 btn-outline-taratura"
                  type="button"
                >
                  <span
                    className="material-icons me-2"
                    style={{
                      fontSize: '16px',
                    }}
                  >
                    edit_calendar
                  </span>
                  <span> Pianifica taratura</span>
                </Button>
              </MobileActionsMenu>
            </div>
          </div>
        </div>
        <div className="col-12 mt-2 filters-block">
          <div
            className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded list-toolbar"
            style={{ overflowX: 'auto' }}
          >
            <ListFilters active={areFiltersActive(uiFilters)}>
              <div className="form-group d-flex align-items-center">
                <label className="label-m text-body-secondary me-2 ms-2">Periodo</label>
                <DateRangeField
                  start={filters.data_intervento__gte}
                  end={filters.data_intervento__lte}
                  placeholder="Filtra per data intervento"
                  onChange={(start, end) => setFilters({ data_intervento__gte: start, data_intervento__lte: end, page: 1 })}
                />
              </div>
              <div className="d-flex align-items-center ms-3">
                <label className="label-m text-body-secondary me-2">Cliente</label>
                <div style={{ width: '230px' }}>
                  <SelectAutoCompleteFilter
                    className="interventi-cliente-filter"
                    value={uiFilters.cliente ?? ''}
                    placeholder="Cerca cliente"
                    onChange={(value: any) => {
                      if (!value) {
                        return setFilters({
                          cliente: '',
                          page: 1,
                        })
                      } else {
                        setFilters({
                          cliente: value.value,
                          page: 1,
                        })
                      }
                    }}
                    url={`${API_URL}/bilance/cliente/options`}
                    isMulti={false}
                  />
                </div>
              </div>
              <div className="d-flex align-items-center ms-3">
                <label className="label-m text-body-secondary me-2">Tipo</label>

                <select
                  className={classNames('form-select me-2', {
                    'text-secondary': uiFilters.tipo_intervento !== '',
                  })}
                  style={{ width: '190px' }}
                  value={uiFilters.tipo_intervento}
                  onChange={(e) => setFilters({ tipo_intervento: e.target.value, page: 1 })}
                >
                  <option value="">Tipo intervento</option>
                  {TIPI_INTERVENTO.map((tipo) => (
                    <option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex align-items-center ms-3">
                <label className="label-m text-body-secondary me-2">Stato</label>
                <select
                  className={classNames('form-select me-2', {
                    'text-secondary': uiFilters.stato !== '',
                  })}
                  style={{ width: '170px' }}
                  value={uiFilters.stato}
                  onChange={(e) => setFilters({ stato: e.target.value, page: 1 })}
                >
                  <option value="">Stato intervento</option>
                  {STATI_INTERVENTO.map((stato) => (
                    <option key={stato.value} value={stato.value}>
                      {stato.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex align-items-center ms-3">
                <label className="label-m text-body-secondary me-2">Esito</label>
                <select
                  className={classNames('form-select me-2', {
                    'text-secondary': uiFilters.esito !== '',
                  })}
                  style={{ width: '170px' }}
                  value={uiFilters.esito}
                  onChange={(e) => setFilters({ esito: e.target.value, page: 1 })}
                >
                  <option value="">Esito intervento</option>
                  {ESITI.map((esito) => (
                    <option key={esito.value} value={esito.value}>
                      {esito.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex align-items-center ms-3">
                <label className="label-m text-body-secondary me-2">Bilancia</label>
                <input
                  className={classNames('form-control me-2')}
                  style={{ width: '170px' }}
                  value={uiFilters.bilancia}
                  onChange={(e) => setFilters({ bilancia: e.target.value, page: 1 })}
                />
              </div>
            </ListFilters>
          </div>
        </div>
        <div className="col-12">
          <InterventiTable interventi={interventi} />
          <div className="position-relative">
            <div
              className="position-absolute d-flex align-items-center list-footer-side"
              style={{
                top: '50%',
                left: 20,
                transform: 'translateY(-50%)',
              }}
            >
              <Button
                variant={
                  uiFilters.data_intervento__gte === dayjs().format('YYYY-MM-DD') &&
                  uiFilters.data_intervento__lte === dayjs().format('YYYY-MM-DD')
                    ? 'primary'
                    : 'outline-primary'
                }
                className="d-flex align-items-center me-2"
                type="button"
                onClick={() => {
                  // go to today
                  setFilters({
                    page: 1,
                    search: '',
                    ordering: 'data',
                    tipo_intervento: '',
                    stato: '',
                    esito: '',
                    cliente: '',
                    data_intervento__gte: dayjs().format('YYYY-MM-DD'),
                    data_intervento__lte: dayjs().format('YYYY-MM-DD'),
                  })
                }}
              >
                <MaterialIcon icon="today" className="me-2" />
                <span> Oggi</span>
              </Button>
              {areFiltersActive(uiFilters) && (
                <Button
                  variant="outline-secondary"
                  className="d-flex align-items-center"
                  type="button"
                  onClick={() => {
                    setFilters({
                      page: 1,
                      search: '',
                      ordering: 'data',
                      tipo_intervento: '',
                      stato: '',
                      esito: '',
                      cliente: '',
                      data_intervento__gte: '',
                      data_intervento__lte: '',
                    })
                  }}
                >
                  <span className="material-icons me-2">filter_alt_off</span>
                  <span>Reset filtri</span>
                </Button>
              )}
            </div>
            <Paginator
              count={interventi?.data?.count ?? 0}
              currentPage={uiFilters.page}
              goToPage={(page) => setFilters({ page })}
            />
            <div
              className="me-2 text-nowrap position-absolute list-count list-footer-side"
              style={{
                // bottom: c
                right: 20,
                // center in the middle of paginator
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              Stai visualizzando {interventi?.data?.count ?? 0} di {interventi?.data?.full_count ?? 0} interventi
            </div>
          </div>
        </div>
      </div>
      {modalStatePianificaPeriodica.isOpen && (
        <ModalPianificazionePeriodica
          isOpen={modalStatePianificaPeriodica.isOpen}
          toggle={modalActionsPianificaPeriodica.toggle}
          save={async (values: any) => {
            await fetchApi(`${API_URL}/bilance/intervento/pianifica/`, {
              method: 'POST',
              body: JSON.stringify(values),
              headers: {
                Authorization: `Bearer ${tokens?.access}`,
                'Content-Type': 'application/json',
              },
            }).then(() => {
              interventi.refetch()
              modalActionsPianificaPeriodica.close()
            })
          }}
        />
      )}
      {modalStatePianificaTaratura.isOpen && (
        <ModalPianificazioneTaratura
          isOpen={modalStatePianificaTaratura.isOpen}
          toggle={modalActionsPianificaTaratura.toggle}
          save={async (values: any) => {
            await fetchApi(`${API_URL}/bilance/intervento/pianifica-tarature/`, {
              method: 'POST',
              body: JSON.stringify(values),
              headers: {
                Authorization: `Bearer ${tokens?.access}`,
                'Content-Type': 'application/json',
              },
            }).then(() => {
              interventi.refetch()
              modalActionsPianificaTaratura.close()
            })
          }}
        />
      )}
    </div>
  )
}
